import AppBusy, { type BusyHold, useBusy } from "@core-ui/AppBusy";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { useSyncCount } from "@core-ui/ContextServices/SyncCount/useSyncCount";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { useIsEnterprise } from "@ext/enterprise/utils/useIsEnterprise";
import SyncLayout from "@ext/git/actions/Sync/components/SyncLayout";
import { useAvailableSync } from "@ext/git/actions/Sync/logic/hooks/useAvailableSync";
import SyncService from "@ext/git/actions/Sync/logic/SyncService";
import t from "@ext/localization/locale/translate";
import useSourceData from "@ext/storage/components/useSourceData";
import { useOpenRestoreSourceTokenModal } from "@ext/storage/logic/SourceDataProvider/components/useOpenRestoreSourceTokenModal";
import { useOpenStorageNotConnectedModal } from "@ext/storage/logic/SourceDataProvider/components/useOpenStorageNotConnectedModal";
import { useCallback, useEffect } from "react";

const Sync = ({ disable }: { disable?: boolean }) => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const catalogName = useCatalogPropsStore((state) => state.data?.name);
	const syncProcess = useBusy("sync");

	const { syncCount, updateSyncCount } = useSyncCount(catalogName);

	const source = useSourceData();
	const isEnterprise = useIsEnterprise();

	const openRestoreSourceModal = useOpenRestoreSourceTokenModal(source);
	const openStorageNotConnectedModal = useOpenStorageNotConnectedModal();

	const availableSync = useAvailableSync();
	const disabled = disable || !availableSync;
	// Neither state is readable from the icon: it looks the same grey whether the app is offline or
	// the user is looking at history.
	const disabledReason = !availableSync
		? t("git.sync.error.at-revision")
		: disable
			? t("disabled-reason.sync-offline")
			: undefined;

	// biome-ignore lint/correctness/useExhaustiveDependencies: it's ok
	useEffect(() => {
		// null = "no active hold": a second stop-like event (finish + error can both arrive)
		// must not double-release, and the next sync cycle's ??= must create a fresh hold.
		let hold: BusyHold | null = null;

		const handleSyncStart = () => {
			hold ??= AppBusy.hold("sync");
		};

		const handleSyncFinish = () => {
			hold?.release();
			hold = null;
			updateSyncCount({
				pull: 0,
				push: 0,
				changed: 0,
				hasChanges: false,
				errorMessage: null,
			});
		};

		const handleSyncError = () => {
			hold?.release();
			hold = null;
		};

		const startToken = SyncService.events.on("start", handleSyncStart);
		const finishToken = SyncService.events.on("finish", handleSyncFinish);
		const errorToken = SyncService.events.on("error", handleSyncError);
		const conflictAbortedToken = SyncService.events.on("conflict-aborted", () => {
			hold?.release();
			hold = null;
		});

		return () => {
			SyncService.events.off(startToken);
			SyncService.events.off(finishToken);
			SyncService.events.off(errorToken);
			SyncService.events.off(conflictAbortedToken);
			// Unmounting mid-sync (e.g. closing the catalog before it settles): nothing will ever
			// fire finish/error again to release this, so release it here.
			hold?.release();
			hold = null;
		};
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: apiUrlCreator is a reactive context value hidden behind a service getter
	const handleSyncClick = useCallback(async () => {
		if (source?.isInvalid) {
			if (isEnterprise) {
				openStorageNotConnectedModal();
				return;
			}
			openRestoreSourceModal();
			return;
		}

		await SyncService.sync(apiUrlCreator);
	}, [apiUrlCreator, source?.isInvalid, openRestoreSourceModal, openStorageNotConnectedModal, isEnterprise]);

	return (
		<SyncLayout
			disabled={disabled}
			disabledReason={disabledReason}
			onClick={handleSyncClick}
			pullCounter={syncCount?.pull || 0}
			pushCounter={syncCount?.push || 0}
			sourceInvalid={source?.isInvalid}
			syncProccess={syncProcess}
		/>
	);
};

export default Sync;
